import { createFileRoute } from "@tanstack/solid-router";
import { seo } from "~/utils/seo";

export const Route = createFileRoute("/_main/terms")({
  head: () => ({
    meta: seo({
      title: "Terms and Conditions | ActShark",
      description:
        "Terms and conditions for using ActShark accounting software, including subscription, billing, and usage terms.",
      path: "/terms",
    }),
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <main class="flex min-h-screen flex-col">
      <div class="mx-auto w-full max-w-4xl px-6 py-12 md:py-16 lg:py-20">
        <h1 class="mb-2 text-4xl font-bold">ActShark Terms and Conditions</h1>
        <p class="mb-8 text-sm text-muted-foreground">Effective: Sep 4, 2026</p>

        <p class="mb-6 leading-relaxed text-muted-foreground">
          These Terms and Conditions ("Terms") govern your access to and use of
          ActShark accounting software and related services, including the
          website at actshark.com (collectively, the "Service").{" "}
          <span class="font-semibold text-foreground">
            Note: ActShark is currently operated by its owner as an individual;
            contracting entity details will be published here upon
            incorporation.
          </span>
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          By creating an account, accessing, or using the Service, you agree to
          be bound by these Terms and our{" "}
          <a href="/privacy" class="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          . If you use the Service on behalf of a company or other legal entity,
          you represent that you have authority to bind that entity, and "you"
          refers to that entity. If you do not agree to these Terms, do not use
          the Service.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">1. Definitions</h2>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            <strong>"ActShark", "we", "us"</strong> — the operator of the
            Service, as identified in the notice above.
          </li>
          <li>
            <strong>"Service"</strong> — the ActShark accounting software,
            including the website, dashboard, AI features, bank connections,
            document processing, API, and any related services.
          </li>
          <li>
            <strong>"Account"</strong> — your registered account with the
            Service.
          </li>
          <li>
            <strong>"Your Data"</strong> — the financial records, documents, and
            other content you upload to or create in the Service.
          </li>
          <li>
            <strong>"Plan"</strong> — a subscription tier (for example, Cloud
            Personal, Cloud Basic, Cloud Pro, Cloud Business) with its included
            features and usage allowances.
          </li>
          <li>
            <strong>"Usage Charges"</strong> — metered charges for usage beyond
            your Plan's included allowances, such as AI credits, storage, bank
            connections, or additional businesses.
          </li>
        </ul>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          2. Subscriptions, Billing, and Auto-Renewal
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          Parts of the Service are offered on a subscription basis. By
          purchasing a subscription, you agree to the following:
        </p>
        <ul class="mb-6 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            <strong>Auto-Renewal:</strong> Subscriptions automatically renew at
            the end of each billing cycle unless you cancel before the renewal
            date.
          </li>
          <li>
            <strong>Payment:</strong> You authorize us (through our payment
            processor, Stripe) to charge your chosen payment method for
            subscription fees, applicable taxes, and any Usage Charges incurred
            during each billing period.
          </li>
          <li>
            <strong>Usage Charges:</strong> Usage beyond your Plan's included
            allowances is billed on a metered basis at the rates published in
            the Service at the time the usage occurs.
          </li>
          <li>
            <strong>Failed Payments:</strong> If a payment fails, we may retry
            the charge, suspend access to paid features, and/or downgrade your
            account until payment is resolved.
          </li>
          <li>
            <strong>Taxes:</strong> Prices are exclusive of taxes unless stated
            otherwise. You are responsible for applicable sales, use, VAT, or
            similar taxes, which may be added to your charges.
          </li>
        </ul>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          3. Cancellations and Refunds
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          You may cancel your subscription at any time from your billing
          settings or by contacting us. Cancellation stops future renewals; your
          Plan remains active until the end of the current billing period, and
          we generally do not provide prorated or partial refunds for the
          remainder of that period.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Payments are processed by Stripe and are subject to Stripe's terms.
          Refunds, if any, are issued at our sole discretion except where
          required by law. Usage Charges already incurred are non-refundable.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          4. Changes to Prices, Plans, and the Service
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          We reserve the right to change, modify, or discontinue any part of the
          Service, including Plans, features, included usage allowances, overage
          rates, and prices, at any time.
        </p>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          If we change the price of your subscription, we will provide advance
          notice by email and/or a notice within the Service before the change
          takes effect. The new price applies from your next billing cycle after
          the effective date. If you do not agree to the new price, you may
          cancel before the change takes effect and you will not be charged the
          new price.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Changes to features or usage allowances may take effect immediately
          unless they would reduce the functionality of an active paid
          subscription, in which case we will provide reasonable notice. Your
          continued use of the Service after a change takes effect constitutes
          acceptance of the change.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          5. License and Acceptable Use
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          We grant you a limited, non-exclusive, non-transferable, revocable
          license to use the Service for your internal business purposes during
          your subscription term, subject to these Terms. You agree not to:
        </p>
        <ul class="mb-4 ml-4 list-disc space-y-2 text-muted-foreground">
          <li>
            use the Service for any unlawful purpose or in violation of any
            applicable law or regulation;
          </li>
          <li>
            resell, sublicense, or provide access to the Service to third
            parties without our written permission;
          </li>
          <li>
            scrape, harvest, or attempt to circumvent usage limits, credits,
            quotas, or rate limits;
          </li>
          <li>
            reverse engineer the Service except as permitted by applicable law;
          </li>
          <li>
            interfere with or disrupt the Service, or attempt to gain
            unauthorized access to any systems or data;
          </li>
          <li>
            upload malicious code or content you do not have the rights to use.
          </li>
        </ul>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          You are responsible for maintaining the confidentiality of your
          account credentials and for all activity under your Account. We may
          suspend or terminate accounts that violate these rules, with or
          without notice, where reasonably necessary to protect the Service or
          other users.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          6. Intellectual Property and Your Data
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          The Service, including its software, design, text, graphics, and
          trademarks, is owned by ActShark or its licensors and is protected by
          copyright and other intellectual property laws. Except for the license
          above, no rights in the Service are transferred to you.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          You retain all ownership rights to Your Data. You grant us a limited
          license to host, process, and transmit Your Data solely as necessary
          to provide the Service to you. You are solely responsible for the
          accuracy, legality, and compliance of Your Data.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          7. No Professional Accounting Advice; AI Output
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          ActShark is a software tool. It does not provide accounting, legal, or
          tax advice, and its outputs (including AI-assisted categorization,
          extraction, and reports) may contain errors or incomplete information.
          You are solely responsible for verifying the accuracy of your books
          and for complying with your accounting, tax, and reporting
          obligations, and you should consult a qualified professional where
          appropriate.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          8. Disclaimers; Limitation of Liability
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          The Service is provided "as is" and "as available" without warranties
          of any kind, whether express or implied, including merchantability,
          fitness for a particular purpose, and non-infringement. We do not
          guarantee uninterrupted or error-free access to the Service. Some
          jurisdictions do not allow limitations on implied warranties, so these
          limitations may not apply to you.
        </p>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          To the maximum extent permitted by law, ActShark's total aggregate
          liability arising out of or relating to the Service or these Terms is
          limited to the amounts you paid to us for the Service in the twelve
          (12) months preceding the event giving rise to the claim. We will not
          be liable for any indirect, incidental, special, consequential, or
          punitive damages, or for lost profits, lost data, or business
          interruption, even if advised of the possibility of such damages.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          You agree to indemnify and hold ActShark, its owner, employees, and
          affiliates harmless from any claims, damages, losses, or liabilities
          (including reasonable legal fees) arising out of or related to your
          use of the Service, Your Data, or your violation of these Terms.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          9. Availability, Suspension, and Termination
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          We may perform maintenance, and we may modify, suspend, or discontinue
          any part of the Service at any time. If we terminate your paid
          subscription for reasons other than your breach of these Terms, we
          will refund the unused, prepaid portion of the current billing period.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          Upon termination of your Account for any reason, your right to use the
          Service ceases immediately. Following Account closure, we may delete
          Your Data in accordance with our data retention practices, and it is
          your responsibility to export Your Data beforehand.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          10. Dispute Resolution; Arbitration
        </h2>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          These Terms are governed by the laws of the State of Colorado and the
          United States, without regard to conflict of law provisions. Before
          filing a claim, you agree to contact us at hello@actshark.com and
          attempt good-faith informal resolution for 30 days.
        </p>
        <p class="mb-4 leading-relaxed text-muted-foreground">
          <strong>
            Please read this section carefully — it affects your legal rights.
          </strong>{" "}
          Except for small claims court actions and claims seeking injunctive
          relief for intellectual property infringement, any dispute arising out
          of or relating to these Terms or the Service will be resolved by
          binding individual arbitration administered under the rules of a
          recognized arbitration provider seated in Denver, Colorado. You and
          ActShark each waive the right to a trial by jury and to participate in
          a class action, class-wide arbitration, or other representative
          proceeding. If this arbitration agreement is found unenforceable, the
          dispute will be brought exclusively in the state or federal courts
          located in Denver, Colorado, and you consent to their jurisdiction.
        </p>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          You may opt out of arbitration by emailing hello@actshark.com within
          30 days of first accepting these Terms, stating your name and that you
          opt out of arbitration. Opting out does not affect any other part of
          these Terms.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">
          11. Changes to These Terms
        </h2>
        <p class="mb-6 leading-relaxed text-muted-foreground">
          We may revise these Terms at any time. Material changes will be
          communicated by email and/or a notice within the Service at least
          fifteen (15) days before they take effect (except for changes required
          by law or to address security or abuse, which may take effect
          immediately). The "Effective" date at the top identifies the current
          version. By continuing to use the Service after changes take effect,
          you agree to be bound by the updated Terms. If you do not agree, you
          must stop using the Service and may cancel your subscription.
        </p>

        <h2 class="mb-4 mt-10 text-2xl font-bold">12. Contact</h2>
        <p class="leading-relaxed text-muted-foreground">
          Questions about these Terms can be sent to{" "}
          <a
            href="mailto:hello@actshark.com"
            class="text-blue-600 hover:underline"
          >
            hello@actshark.com
          </a>
          .
        </p>
      </div>
    </main>
  );
}
